'use client';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { Survey } from 'survey-react-ui';

import './styles.css';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { FullPageLoading } from '@/components/global/FullPageLoading';
import COOKIE_KEYS from '@/consts/cookieKeys';
import { TreatmentCategoryIds } from '@/consts/treatmentCategories';
import { getEventFlowValueFromVisitType } from '@/helpers/visitUtils';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { type SurveyJsResource } from '@/services/funnelService/types';
import { getVisit } from '@/services/visitsService/client';
import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import cookieStorage from '@/utils/cookies/client';

import BackButton from './surveyjs/components/buttons/back';
import ForwardButton from './surveyjs/components/buttons/forward';
import { useSurvey } from './surveyjs/hooks/useSurvey';
import { useNavigation } from '../../../hooks/useNavigation';

const SurveyJsSurvey = ({ survey }: { survey: SurveyJsResource }) => {
  const { visit } = useVisitContext();
  const { onNext, onBack } = useNavigation();
  const {
    model,
    hideNavigation,
    hideBackNavigation,
    isFirstPage,
    isSaving,
    isSaved,
    isReady,
    disclaimer,
  } = useSurvey(survey);

  const { treatmentCategory, brand } = useFlowContext();
  const { handleIfSessionExpired } = useSessionExpired(brand);
  const sessionCheckFiredRef = useRef(false);

  useEffect(() => {
    if (!isSaved) return;
    if (sessionCheckFiredRef.current) return;
    sessionCheckFiredRef.current = true;

    if (!visit?.uuid) {
      onNext();
      return;
    }

    getVisit({ uuid: visit.uuid, brand: visit.brand })
      .then(() => {
        onNext();
      })
      .catch((error) => {
        const status =
          error instanceof AxiosError ? error.response?.status : undefined;
        if (!handleIfSessionExpired(status)) {
          onNext();
        }
      });
  }, [isSaved, onNext, visit?.uuid, handleIfSessionExpired]);

  useEffect(() => {
    trackEvent(AnalyticsEvent.SURVEY_STARTED, {
      flow: getEventFlowValueFromVisitType(visit),
      funnelId: cookieStorage.get(COOKIE_KEYS.funnelId) ?? '',
      group: survey.group,
      surveyEngine: survey.provider,
      treatmentCategories: treatmentCategory!,
      treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory!],
      version: survey.version,
    });
  }, [survey.group, survey.provider, survey.version, treatmentCategory, visit]);

  if (isSaving || isSaved || !isReady) {
    return <FullPageLoading />;
  }

  return (
    <div
      className={clsx(
        survey.group === 'primary-skincare' ? null : 'data-hj-suppress',
      )}
      translate="no"
    >
      <Survey model={model} />
      {!hideNavigation && (
        <div className="cur-flex cur-flex-col cur-gap-y-2 cur-max-w-[375px]">
          <ForwardButton model={model} />
          {!hideBackNavigation && (
            <BackButton
              model={model}
              isFirstPage={isFirstPage}
              onFirstPageBack={onBack}
            />
          )}
        </div>
      )}
      {disclaimer && (
        <Typography className="cur-max-w-[375px] cur-mt-8" size="meta">
          {disclaimer}
        </Typography>
      )}
    </div>
  );
};

export default SurveyJsSurvey;
